import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Square, Plane, TrainFront, Lock, ArrowUpDown, Users, Briefcase,
  Minus, Plus, AlertCircle, ChevronRight, ArrowRight,
} from "lucide-react";
import PlacePickerModal from "@/components/PlacePickerModal";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import DateTimeFields from "@/components/DateTimeFields";
import { NativeSelect } from "@/components/Form";
import { PENDING_RIDE_KEY } from "@/components/BookWidget";
import { api } from "@/lib/api";
import { getRouteEstimate } from "@/lib/location";
import { AIRLINES } from "@/lib/vehicles";
import { cn } from "@/lib/format";

const isAirport = (p) => !!p && !!p.airport;
const isStation = (p) => !!p && !!p.station;
const isHub = (p) => isAirport(p) || isStation(p);

function Counter({ icon: Icon, label, value, onDec, onInc, testID }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
      <span className="flex items-center gap-2 font-semibold text-foreground"><Icon className="h-5 w-5 text-brand-600" /> {label}</span>
      <div className="flex items-center gap-4">
        <button onClick={onDec} data-testid={`${testID}-minus`} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground"><Minus className="h-4 w-4" /></button>
        <span data-testid={`${testID}-count`} className="w-6 text-center font-num text-xl font-bold text-foreground">{value}</span>
        <button onClick={onInc} data-testid={`${testID}-plus`} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground"><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function HeroBooking({ guest = true }) {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [scheduledAt, setScheduledAt] = useState("");
  const [picker, setPicker] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [airline, setAirline] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [bags, setBags] = useState(1);
  const [routeInfo, setRouteInfo] = useState(null);

  // Live distance + ETA estimate once both ends are chosen (map is a static hero image).
  useEffect(() => {
    let active = true;
    if (!pickup || !destination) { setRouteInfo(null); return; }
    getRouteEstimate(pickup, destination).then((r) => { if (active) setRouteInfo(r); });
    return () => { active = false; };
  }, [pickup?.lat, pickup?.lng, destination?.lat, destination?.lng]);

  const fromAirport = isAirport(pickup);
  const hubIsAirport = isAirport(pickup) || isAirport(destination);
  const isHubTrip = isHub(pickup) || isHub(destination);
  const detailsReady = isHubTrip && (!hubIsAirport || (!!airline && (!fromAirport || flightNumber.trim().length >= 2)));

  const swap = () => { const p = pickup; setPickup(destination); setDestination(p); };

  const openDetails = () => {
    setError(null);
    if (!pickup || !destination) return setError("Please set your pickup and drop-off locations.");
    if (isHub(pickup) && isHub(destination)) return setError("Only one end of the trip can be the airport or station.");
    if (!isHubTrip) return setError("One end of your trip must be Orlando International Airport (MCO) or Brightline Orlando Station.");
    if (!scheduledAt) return setError("Please choose a pickup date and time.");
    const t = new Date(scheduledAt).getTime();
    if (t < Date.now() + 24 * 3600e3) return setError("Rides must be booked at least 24 hours in advance.");
    if (t > Date.now() + 90 * 864e5) return setError("Rides can be scheduled up to 90 days in advance.");
    setDetailsOpen(true);
  };

  const confirm = async () => {
    if (!detailsReady) return;
    const payload = {
      pickup, destination, stops: [], when: "scheduled",
      scheduled_time: new Date(scheduledAt).toISOString(),
      passengers, bags,
      origin_url: window.location.origin,
      airport_info: hubIsAirport ? {
        direction: fromAirport ? "from" : "to",
        airline, bags,
        flight_number: fromAirport ? flightNumber.trim() : null,
        terminal: null,
        baggage_claim: null,
      } : null,
    };
    if (guest) {
      localStorage.setItem(PENDING_RIDE_KEY, JSON.stringify(payload));
      navigate("/auth");
      return;
    }
    setLoading(true);
    try {
      const res = await api("/rides", { method: "POST", body: payload });
      navigate(`/ride/${res.ride.id}`);
    } catch (e) {
      setError(e?.message || "Could not schedule this ride.");
      setDetailsOpen(false);
    } finally { setLoading(false); }
  };

  return (
    <section className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-5 pt-10 md:grid-cols-2 md:pt-16">
      {/* Left: headline + form */}
      <div className="relative z-30 animate-fade-up">
        <span className="inline-flex items-center gap-2 text-sm font-bold text-brand-600">
          <Plane className="h-4 w-4" /> Orlando airport specialists
        </span>
        <h1 className="mt-3 text-5xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
          Your Orlando<br />airport ride,<br /><span className="text-brand-600">your price.</span>
        </h1>

        <div className="mt-7 max-w-md space-y-2.5">
          {/* Date & time */}
          <DateTimeFields
            min={new Date(Date.now() + 24 * 3600e3 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
            onChange={(v) => { setScheduledAt(v); setError(null); }}
          />

          {/* Pickup / Dropoff */}
          <div className="relative rounded-xl bg-secondary pr-12">
            <button onClick={() => setPicker("pickup")} data-testid="pickup-row" className="flex w-full items-center gap-3 px-4 py-3.5 text-left">
              {isAirport(pickup) ? <Plane className="h-4 w-4 text-brand-600" /> : isStation(pickup) ? <TrainFront className="h-4 w-4 text-brand-600" /> : <MapPin className="h-4 w-4 text-success" />}
              <span className={cn("truncate text-sm font-medium", pickup ? "text-foreground" : "text-muted-foreground")}>{pickup?.label || "Pickup location"}</span>
              {isHub(pickup) && <Lock className="ml-auto h-4 w-4 text-muted-foreground" />}
            </button>
            <div className="mx-4 border-t border-border" />
            <button onClick={() => setPicker("destination")} data-testid="destination-row" className="flex w-full items-center gap-3 px-4 py-3.5 text-left">
              {isAirport(destination) ? <Plane className="h-4 w-4 text-brand-600" /> : isStation(destination) ? <TrainFront className="h-4 w-4 text-brand-600" /> : <Square className="h-4 w-4 text-brand-600" />}
              <span className="truncate text-sm font-medium text-foreground">{destination?.label || "Where to?"}</span>
              {isHub(destination) && <Lock className="ml-auto h-4 w-4 text-muted-foreground" />}
            </button>
            <button onClick={swap} data-testid="swap-direction" className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-brand-600">
              <ArrowUpDown className="h-4 w-4" />
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive" data-testid="ride-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <div className="flex items-center gap-5 pt-1">
            <Button onClick={openDetails} data-testid="find-rides">Schedule Ride</Button>
            <button onClick={() => navigate("/auth")} data-testid="hero-login" className="text-sm font-semibold text-foreground underline-offset-4 hover:underline">
              Log in to see your recent activity
            </button>
          </div>
        </div>
      </div>

      {/* Right: airport photo with overlay */}
      <div className="relative overflow-hidden rounded-3xl border border-border shadow-card">
        <img
          src="/hero-airport.png"
          alt="Chauffeur loading luggage at Orlando airport"
          className="h-[300px] w-full object-cover md:h-[520px]"
        />
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3 rounded-2xl glass px-5 py-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Ready to travel?</p>
            {routeInfo ? (
              <p className="font-bold text-foreground" data-testid="route-estimate">
                <span className="font-num">{routeInfo.miles.toFixed(1)} mi</span> · about <span className="font-num">{routeInfo.minutes} min</span>
              </p>
            ) : (
              <p className="font-bold text-foreground">Schedule your MCO ride ahead</p>
            )}
          </div>
          <button onClick={openDetails} className="flex shrink-0 items-center gap-1 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.03]">
            Schedule <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Place picker */}
      <PlacePickerModal
        open={picker !== null}
        title={picker === "pickup" ? "Set pickup" : "Set destination"}
        onlyHub={picker === "destination" ? (!!pickup && !isHub(pickup)) : picker === "pickup" ? (!!destination && !isHub(destination)) : false}
        blockHub={picker === "destination" ? isHub(pickup) : picker === "pickup" ? isHub(destination) : false}
        onClose={() => setPicker(null)}
        onSelect={(p) => { setError(null); if (picker === "pickup") setPickup(p); else setDestination(p); }}
      />

      {/* Trip details modal */}
      <Modal open={detailsOpen} onClose={() => setDetailsOpen(false)} title={!hubIsAirport ? "Trip details" : fromAirport ? "Airport pickup details" : "Airport drop-off details"} subtitle="A few details so drivers can bid accurately."
        footer={
          <Button className="w-full" size="lg" onClick={confirm} loading={loading} disabled={!detailsReady} data-testid="confirm-details">
            {guest ? "Continue to sign in" : "Schedule Ride"} <ChevronRight className="h-5 w-5" />
          </Button>
        }>
        <div className="space-y-3">
          {hubIsAirport && (
            <NativeSelect label="Airline" options={AIRLINES} placeholder="Select airline" value={airline} onChange={setAirline} data-testid="airline-select" />
          )}
          {hubIsAirport && fromAirport && (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground/80">Flight #</span>
              <input data-testid="flight-input" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value.toUpperCase())} placeholder="AA1234" className="h-12 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-brand-500" />
            </label>
          )}
          <Counter icon={Users} label="Passengers" value={passengers} onDec={() => setPassengers((p) => Math.max(1, p - 1))} onInc={() => setPassengers((p) => Math.min(6, p + 1))} testID="pax" />
          <Counter icon={Briefcase} label="Bags" value={bags} onDec={() => setBags((b) => Math.max(0, b - 1))} onInc={() => setBags((b) => Math.min(6, b + 1))} testID="bags" />
        </div>
      </Modal>
    </section>
  );
}
